/**
 * BondsResultsMap — geographic visualization for bond CRA qualification
 *
 * Used in two modes (controlled by `properties` prop):
 *   - Portfolio: every property across all bonds in the current analysis,
 *     color-coded by qualification status (qualifies / outside AA / no
 *     affordability data / geocoding failed). The "demo closer" view.
 *   - Drill-down: a single bond's properties, auto-zoomed to that bond's
 *     footprint. Used inside the per-bond drill-down panel.
 *
 * Filtering happens upstream — the parent page passes a pre-filtered
 * properties array. Same filter state drives the table and this map so
 * the analyst only learns one mental model.
 *
 * AA polygons are NOT rendered here. The marker colors already convey
 * inside/outside AA, and rendering tract-level GeoJSON for every AA
 * the bank has would slow the portfolio view substantially. The drill-
 * down view in the CRA module remains the authoritative AA visualizer.
 *
 * Rendering: the shared Mapbox engine (CLARID-UX-2026-06). Properties are
 * drawn as a single GeoJSON circle layer (GPU-rendered — fast for the
 * 50-CUSIP K-deal portfolios analysts paste) rather than per-point DOM
 * markers. Status → color, popups, classification, and export behavior are
 * unchanged from the prior Leaflet implementation; only the engine moved.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { toPng } from 'html-to-image';
import * as Sentry from '@sentry/react';
import { Source, Layer, Popup } from 'react-map-gl/mapbox';
import type { MapRef, MapMouseEvent } from 'react-map-gl/mapbox';
import MapEngine from '../mapping/MapEngine';
// Shared canonical palette (CLARID-UX-2026-06) — colorblind-safe; centralizes map colors.
import { BOND_STATUS_COLORS, BOND_STATUS_LABELS } from '../../lib/mapping/mapColors';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface BondMapProperty {
  propertyId: string;
  propertyName: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  county: string | null;
  censusTract: string | null;
  latitude: number | null;
  longitude: number | null;
  insideAa: boolean;
  matchingAaName: string | null;
  units: number | null;
  lmiUnits: number | null;
  lmiPct: number | null;
  qualifiesForCra: boolean;
  allocatedDollars: number | null;
  reason: string;
  /** Which bond / CUSIP this property rolls up to. Used in the popup
   *  so analysts can recognize which bond they're looking at. */
  cusip: string;
  bondName: string | null;
}

export type BondMapStatus = 'qualifies' | 'outside_aa' | 'no_affordability' | 'no_geocode';

interface BondsResultsMapProps {
  properties: BondMapProperty[];
  /** Compact = used inside drill-down (smaller height, fewer chrome). */
  compact?: boolean;
  className?: string;
  /** When the property list yields zero plottable points, fall back to
   *  this bounding box (typically the bank's branch footprint) so the
   *  map shows meaningful geographic context instead of the whole US.
   *  NOTE: Leaflet corner order [[lat, lng], [lat, lng]] — flipped to
   *  Mapbox [lng, lat] internally. */
  fallbackBounds?: [[number, number], [number, number]] | null;
}

// ─── Status classification ────────────────────────────────────────────────────

// eslint-disable-next-line react-refresh/only-export-components
export function classifyMapStatus(p: BondMapProperty): BondMapStatus {
  if (p.latitude == null || p.longitude == null) return 'no_geocode';
  if (p.qualifiesForCra) return 'qualifies';
  if (!p.insideAa) return 'outside_aa';
  // Inside AA but doesn't qualify — typically because LMI ratio is below
  // threshold (which usually means the property's affordability data
  // hasn't been parsed yet — the K-deal / pre-OCS-extraction case).
  if (p.lmiPct == null) return 'no_affordability';
  return 'outside_aa';
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDollars(n: number | null | undefined): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatPct(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function buildPopupHtml(p: BondMapProperty): string {
  const status = classifyMapStatus(p);
  const statusColor = BOND_STATUS_COLORS[status];
  const statusLabel = BOND_STATUS_LABELS[status];
  const lines: string[] = [];
  lines.push(`<div style="font-family: ui-sans-serif, system-ui; font-size: 12px; line-height: 1.45;">`);
  lines.push(`<div style="font-weight: 600; font-size: 13px; color: #0f172a; margin-bottom: 4px;">${escapeHtml(p.propertyName ?? 'Unnamed property')}</div>`);
  if (p.address) {
    const cityState = [p.city, p.state].filter(Boolean).join(', ');
    lines.push(`<div style="color: #475569; margin-bottom: 6px;">${escapeHtml(p.address)}${cityState ? `<br>${escapeHtml(cityState)}` : ''}</div>`);
  }
  lines.push(`<div style="margin-bottom: 4px;"><span style="display:inline-block; padding: 1px 6px; border-radius: 4px; background:${statusColor}; color: white; font-size: 11px; font-weight: 500;">${statusLabel}</span></div>`);
  lines.push(`<table style="margin-top: 4px; font-size: 11px; border-collapse: collapse;">`);
  if (p.matchingAaName) lines.push(`<tr><td style="color:#64748b; padding-right: 8px;">AA</td><td style="font-weight:500;">${escapeHtml(p.matchingAaName)}</td></tr>`);
  if (p.units != null) lines.push(`<tr><td style="color:#64748b; padding-right: 8px;">Units</td><td>${p.units.toLocaleString()}</td></tr>`);
  if (p.lmiPct != null) lines.push(`<tr><td style="color:#64748b; padding-right: 8px;">LMI %</td><td>${formatPct(p.lmiPct)}</td></tr>`);
  if (p.allocatedDollars != null) lines.push(`<tr><td style="color:#64748b; padding-right: 8px;">Allocated</td><td style="font-weight:500;">${formatDollars(p.allocatedDollars)}</td></tr>`);
  lines.push(`<tr><td style="color:#64748b; padding-right: 8px;">CUSIP</td><td style="font-family:ui-monospace,monospace;">${escapeHtml(p.cusip)}</td></tr>`);
  lines.push(`</table>`);
  if (p.reason) lines.push(`<div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #e2e8f0; color: #475569; font-size: 11px;">${escapeHtml(p.reason)}</div>`);
  lines.push(`</div>`);
  return lines.join('');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function BondsResultsMap({ properties, compact = false, className, fallbackBounds }: BondsResultsMapProps) {
  const mapRef = useRef<MapRef | null>(null);
  const exportRootRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [popupProp, setPopupProp] = useState<BondMapProperty | null>(null);
  const [hover, setHover] = useState<{ x: number; y: number; name: string } | null>(null);

  // Keep a stable rolled-up summary for the legend.
  const summary = useMemo(() => {
    const counts: Record<BondMapStatus, number> = {
      qualifies: 0, outside_aa: 0, no_affordability: 0, no_geocode: 0,
    };
    let qualifiedDollars = 0;
    for (const p of properties) {
      const status = classifyMapStatus(p);
      counts[status]++;
      if (status === 'qualifies') qualifiedDollars += p.allocatedDollars ?? 0;
    }
    return { counts, qualifiedDollars, total: properties.length };
  }, [properties]);

  // Build the GeoJSON point layer + an id→property index for click lookups.
  // Each feature carries only its color + name; the rich popup reads from the
  // indexed BondMapProperty so we don't stringify the whole object into tiles.
  const { pointsGeojson, byId } = useMemo(() => {
    const index = new Map<string, BondMapProperty>();
    const features = [] as Array<{
      type: 'Feature';
      geometry: { type: 'Point'; coordinates: [number, number] };
      properties: { propertyId: string; color: string; name: string };
    }>;
    for (const p of properties) {
      if (p.latitude == null || p.longitude == null) continue;
      index.set(p.propertyId, p);
      const status = classifyMapStatus(p);
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
        properties: {
          propertyId: p.propertyId,
          color: BOND_STATUS_COLORS[status],
          name: p.propertyName ?? 'Property',
        },
      });
    }
    return { pointsGeojson: { type: 'FeatureCollection' as const, features }, byId: index };
  }, [properties]);

  // Auto-fit to the plotted properties (or the fallback footprint) once the
  // map style is ready. Mapbox bounds are [[lng,lat],[lng,lat]].
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    if (pointsGeojson.features.length > 0) {
      let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
      for (const f of pointsGeojson.features) {
        const [lng, lat] = f.geometry.coordinates;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }
      map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 40, maxZoom: 12, duration: 800 });
    } else if (fallbackBounds) {
      // Caller passes Leaflet-order [[lat,lng],[lat,lng]] — flip to Mapbox [lng,lat].
      const [[latA, lngA], [latB, lngB]] = fallbackBounds;
      map.fitBounds([[lngA, latA], [lngB, latB]], { padding: 40, maxZoom: 11, duration: 800 });
    } else {
      map.flyTo({ center: [-98.5795, 39.8283], zoom: 4, duration: 0 });
    }
  }, [pointsGeojson, fallbackBounds, mapLoaded]);

  function handleMapClick(evt: MapMouseEvent) {
    const feature = evt.features?.find((f) => f.layer?.id === 'bond-points');
    if (!feature) {
      setPopupProp(null);
      return;
    }
    const id = feature.properties?.propertyId as string | undefined;
    const p = id ? byId.get(id) : undefined;
    if (p) setPopupProp(p);
  }

  function handleMouseMove(evt: MapMouseEvent) {
    const feature = evt.features?.find((f) => f.layer?.id === 'bond-points');
    const canvas = evt.target.getCanvas();
    if (feature) {
      canvas.style.cursor = 'pointer';
      setHover({ x: evt.point.x, y: evt.point.y, name: (feature.properties?.name as string) ?? 'Property' });
    } else {
      canvas.style.cursor = '';
      setHover(null);
    }
  }

  async function handleExportPng() {
    if (!exportRootRef.current || exporting) return;
    setExporting(true);
    try {
      // Clear popup/hover so they don't appear in the screenshot.
      setPopupProp(null);
      setHover(null);
      await new Promise((r) => setTimeout(r, 50));
      const dataUrl = await toPng(exportRootRef.current, {
        backgroundColor: '#ffffff',
        cacheBust: true,
        pixelRatio: 2,
        filter: (node) =>
          !(node instanceof HTMLElement && node.dataset.exportIgnore === 'true'),
      });
      const stamp = new Date().toISOString().slice(0, 10);
      const link = document.createElement('a');
      link.download = `bonds-map-${stamp}.png`;
      link.href = dataUrl;
      link.click();
    } finally {
      setExporting(false);
    }
  }

  return (
    <div ref={exportRootRef} className={`relative ${className ?? ''}`}>
      <div
        style={{ height: compact ? 360 : 520, width: '100%' }}
        className="rounded-lg overflow-hidden border border-gray-200"
      >
        <MapEngine
          ref={(r) => { mapRef.current = r; }}
          interactiveLayerIds={['bond-points']}
          onLoad={() => setMapLoaded(true)}
          onClick={handleMapClick}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHover(null)}
          onMapError={(err) =>
            Sentry.captureException(err, {
              tags: { component: 'BondsResultsMap', feature: 'bonds_map_mapbox' },
            })
          }
        >
          {pointsGeojson.features.length > 0 && (
            <Source id="bond-points-src" type="geojson" data={pointsGeojson as any}>
              <Layer
                id="bond-points"
                type="circle"
                paint={{
                  'circle-radius': 7,
                  'circle-color': ['get', 'color'],
                  'circle-stroke-width': 2,
                  'circle-stroke-color': '#ffffff',
                  'circle-opacity': 0.9,
                }}
              />
            </Source>
          )}

          {popupProp && popupProp.longitude != null && popupProp.latitude != null && (
            <Popup
              longitude={popupProp.longitude}
              latitude={popupProp.latitude}
              anchor="bottom"
              closeOnClick={false}
              onClose={() => setPopupProp(null)}
              maxWidth="320px"
            >
              <div dangerouslySetInnerHTML={{ __html: buildPopupHtml(popupProp) }} />
            </Popup>
          )}
        </MapEngine>
      </div>

      {/* Hover tooltip — property name, following the cursor. */}
      {hover && (
        <div
          data-export-ignore="true"
          className="pointer-events-none absolute z-[500] -translate-x-1/2 -translate-y-full rounded bg-slate-900/90 px-2 py-1 text-xs font-medium text-white shadow"
          style={{ left: hover.x, top: hover.y - 12 }}
        >
          {hover.name}
        </div>
      )}

      {/* Legend overlay — top-left, clear of the NavigationControl (top-right). */}
      <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg border border-gray-200 shadow-sm p-3 z-[400] text-xs">
        <div className="font-semibold text-gray-900 mb-2">
          {summary.total} {summary.total === 1 ? 'property' : 'properties'}
        </div>
        <ul className="space-y-1.5">
          {(Object.keys(BOND_STATUS_LABELS) as BondMapStatus[]).map((status) => {
            const count = summary.counts[status];
            if (count === 0 && status !== 'qualifies') return null;
            return (
              <li key={status} className="flex items-center gap-2">
                <span
                  className="inline-block w-3 h-3 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: BOND_STATUS_COLORS[status] }}
                />
                <span className="text-gray-700">{BOND_STATUS_LABELS[status]}</span>
                <span className="ml-auto font-semibold text-gray-900 tabular-nums">{count}</span>
              </li>
            );
          })}
        </ul>
        {summary.qualifiedDollars > 0 && (
          <div className="mt-2 pt-2 border-t border-gray-200 text-gray-900 font-semibold">
            {formatDollars(summary.qualifiedDollars)} qualified
          </div>
        )}
      </div>

      {/* Empty state */}
      {properties.length === 0 && (
        <div
          data-export-ignore="true"
          className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-lg z-[300]"
        >
          <div className="text-sm text-gray-500">No properties match the current filters.</div>
        </div>
      )}

      {/* Download PNG — bottom-right corner, free of legend (top-left) and zoom controls (top-right) */}
      <button
        type="button"
        onClick={handleExportPng}
        disabled={exporting || properties.length === 0}
        data-export-ignore="true"
        className="absolute bottom-3 right-3 z-[400] inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        title="Download map as PNG"
      >
        <ArrowDownTrayIcon className="h-3.5 w-3.5" />
        {exporting ? 'Exporting…' : 'Download PNG'}
      </button>
    </div>
  );
}
